import { useState, useMemo, useEffect, useRef } from "react";
import { Loader2, Download, Search, X, RefreshCw } from "lucide-react";
import toast from "react-hot-toast";
import { useMsal } from "@azure/msal-react";
import {
  useFiscalYears,
  useDraftCalendar,
  useDraftBusinesses,
  useDraftMeetingTypes,
  useAttendeeLevels,
  useLocations,
  useUpdateShowInGRC,
  useBulkUpdateStatus,
  useUpdateSignOffAction,
  useBulkDraftPushToSummit,
  draftCalendarKeys
} from "@/services/draftCalendar";
import { useIsAdmin } from "@/hooks/useCurrentUser";
import { usePermissions, useCanAddMeeting, useCanEditMeeting, useCanPerformBulkAction, useCanViewToggleSettings } from "@/hooks/usePermissions";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { DraftCalendarTable } from "./components/DraftCalendarTable";
import { MeetingFlyout } from "./components/MeetingFlyout";
import { BulkActionMenu } from "./components/BulkActionMenu";
import { BulkChangeStatusDialog, BULK_STATUS_CONSTANTS, type BulkChangeStatusValues } from "./components/BulkChangeStatusDialog";
import { BulkChangeSignOffStatusDialog, type BulkSignOffValues } from "./components/BulkChangeSignOffStatusDialog";
import { BulkPushToSummitResultDialog } from "./components/BulkPushToSummitResultDialog";
import type {
  DraftCalendarItem,
  BulkActionInValidStatusResponse,
  BulkDraftPushToSummitRequest,
  BulkUpdateStatusRequest
} from "@/types/draftCalendar";

export function DraftCalendarPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { accounts } = useMsal();
  const userEmail = accounts[0]?.username ?? "";
  const queryClient = useQueryClient();
  const currentUser = useCurrentUser();
  const isAdmin = useIsAdmin();
  const { hasPermission } = usePermissions();
  const canAddMeeting = useCanAddMeeting();
  const canEditMeeting = useCanEditMeeting();
  const canBulkAction = useCanPerformBulkAction();
  const canToggleSettings = useCanViewToggleSettings();
  const canSubmit = hasPermission("CanSubmitToApprover");
  const isScheduler = currentUser?.roleName?.toLowerCase() === "scheduler";
  const userRole = currentUser?.roleName?.toLowerCase() ?? "";
  const userId = currentUser?.id ?? 0;

  // ── Reference data ──────────────────────────────────────────────────────────
  // Use ?? (not = default) so that a null response from the API is also coerced
  // to an empty collection. Destructuring defaults only fire for undefined.
  const { data: fiscalYears = [], isPending: loadingFY } = useFiscalYears();
  const { data: businessesData } = useDraftBusinesses();
  const businesses: Record<string, string[]> = businessesData ?? {};
  const { data: meetingTypesData } = useDraftMeetingTypes();
  const meetingTypesMap: Record<string, string[]> = meetingTypesData ?? {};
  const { data: attendeeLevelsData } = useAttendeeLevels();
  const attendeeLevels: string[] = attendeeLevelsData ?? [];
  const { data: locationsData } = useLocations();
  const locations: string[] = locationsData ?? [];

  // ── Fiscal year selection ───────────────────────────────────────────────────
  const defaultFY = useMemo(
    () => fiscalYears.find((fy) => fy.submissionStatus !== "Closed") ?? fiscalYears[0],
    [fiscalYears]
  );
  const [selectedFYId, setSelectedFYId] = useState<number>(0);
  const activeFYId = selectedFYId || defaultFY?.id || 0;
  const activeFY = fiscalYears.find((fy) => fy.id === activeFYId);

  // ── Data loading ────────────────────────────────────────────────────────────
  const { data: draftItems = [], isPending, isFetching, refetch } =
    useDraftCalendar(activeFYId, userEmail);

  useEffect(() => {
    const createParam = searchParams.get("create");
    const meetingIdParam = searchParams.get("meetingId");
    if (!createParam && !meetingIdParam) return;

    const clearDeepLinkParams = () => {
      const next = new URLSearchParams(searchParams);
      next.delete("create");
      next.delete("meetingId");
      next.delete("mode");
      setSearchParams(next, { replace: true });
    };

    if (createParam === "true") {
      if (!canAddMeeting) {
        toast.error("You do not have permission to add meetings.");
        clearDeepLinkParams();
        return;
      }

      setFlyoutMeetingId(0);
      setFlyoutViewOnly(false);
      setLockedBy(null);
      clearDeepLinkParams();
      return;
    }

    const targetMeetingId = Number(meetingIdParam);
    if (Number.isNaN(targetMeetingId) || targetMeetingId <= 0) {
      clearDeepLinkParams();
      return;
    }

    if (isPending) return;

    const targetMeeting = draftItems.find((item) => item.id === targetMeetingId);
    if (!targetMeeting) {
      toast.error("Requested draft meeting was not found for the selected fiscal year.");
      clearDeepLinkParams();
      return;
    }

    const modeParam = searchParams.get("mode");
    const shouldOpenInEditMode = modeParam === "edit" && canEditMeeting;

    setFlyoutMeetingId(targetMeeting.id);
    setFlyoutViewOnly(!shouldOpenInEditMode);
    setLockedBy(null);
    clearDeepLinkParams();
  }, [canEditMeeting, draftItems, isPending, searchParams, setSearchParams]);

  // ── Derived calendar state (mirrors Blazor LoadDraftCalender) ───────────────
  const signoffReqSubmitted = draftItems.some((i) => i.signoffRequestSubmitted);
  const signOffProcessStarted = draftItems.some((i) => i.isSignOffProcessStarted);
  const responsibleForSignOff = draftItems.some((i) => i.canSubmitSignOff) || isAdmin || isScheduler;
  const isFYClosed = activeFY?.submissionStatus === "Closed";

  // ── UI state ────────────────────────────────────────────────────────────────
  const [searchTerm, setSearchTerm] = useState("");
  const [selected, setSelected] = useState<Set<number>>(new Set());

  // Flyout
  const [flyoutMeetingId, setFlyoutMeetingId] = useState<number | null>(null);
  const [flyoutViewOnly, setFlyoutViewOnly] = useState(true);
  const [lockedBy, setLockedBy] = useState<string | null>(null);

  // ── Bulk Action mutations ───────────────────────────────────────────────────
  const updateShowInGRC = useUpdateShowInGRC();
  const bulkUpdateStatus = useBulkUpdateStatus();
  const updateSignOffAction = useUpdateSignOffAction();
  const bulkDraftPushToSummit = useBulkDraftPushToSummit();

  // Bulk dialogs
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [showSignOffDialog, setShowSignOffDialog] = useState(false);
  const [showPushConfirm, setShowPushConfirm] = useState(false);
  const [pendingPushPayload, setPendingPushPayload] = useState<BulkDraftPushToSummitRequest | null>(null);
  const [showPushResult, setShowPushResult] = useState(false);
  const [pushTotalCount, setPushTotalCount] = useState(0);
  const [pushSuccessCount, setPushSuccessCount] = useState(0);
  const [failedMeetings, setFailedMeetings] = useState<BulkActionInValidStatusResponse[]>([]);
  const pushConfirmPrimaryActionRef = useRef<HTMLButtonElement | null>(null);
  const pushConfirmPreviousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (showPushConfirm) {
      pushConfirmPreviousFocusRef.current = document.activeElement as HTMLElement | null;
      requestAnimationFrame(() => {
        pushConfirmPrimaryActionRef.current?.focus();
      });
      return;
    }

    pushConfirmPreviousFocusRef.current?.focus();
  }, [showPushConfirm]);

  // ── Handlers ────────────────────────────────────────────────────────────────

  function openCreate() {
    setFlyoutMeetingId(0);
    setFlyoutViewOnly(false);
    setLockedBy(null);
  }

  function openView(item: DraftCalendarItem) {
    setFlyoutMeetingId(item.id);
    setFlyoutViewOnly(true);
    setLockedBy(null);
  }

  function handleFlyoutSaved() {
    setFlyoutMeetingId(null);
    void refetch();
  }

  function handleFlyoutClose() {
    setFlyoutMeetingId(null);
  }

  async function handleBulkAction(actionId: string) {
    const selectedItems = draftItems.filter((item) => selected.has(item.id));
    if (selectedItems.length === 0) {
      toast.error("Select at least one meeting.");
      return;
    }

    try {
      if (actionId === "add-grc") {
        const eligibleIds = selectedItems
          .filter((item) => item.state !== BULK_STATUS_CONSTANTS.STATUS_REMOVED && item.state !== BULK_STATUS_CONSTANTS.STATUS_DU_DECLINED)
          .map((item) => item.id);

        if (eligibleIds.length === 0) {
          toast.error("Selected meetings cannot be updated as they are in Removed or DU Declined state.");
          return;
        }

        await updateShowInGRC.mutateAsync({ softHoldIds: eligibleIds, showInGRC: true });
        toast.success("Added to GRC.");
      } else if (actionId === "remove-grc") {
        const eligibleIds = selectedItems
          .filter((item) => item.state !== BULK_STATUS_CONSTANTS.STATUS_REMOVED && item.state !== BULK_STATUS_CONSTANTS.STATUS_DU_DECLINED)
          .map((item) => item.id);

        if (eligibleIds.length === 0) {
          toast.error("Selected meetings cannot be updated as they are in Removed or DU Declined state.");
          return;
        }

        await updateShowInGRC.mutateAsync({ softHoldIds: eligibleIds, showInGRC: false });
        toast.success("Removed from GRC.");
      } else if (actionId === "change-status") {
        setShowStatusDialog(true);
        return;
      } else if (actionId === "change-signoff") {
        const eligibleMeetings = selectedItems.filter((item) => item.signOffStatus?.toLowerCase() !== "signed off");
        if (!eligibleMeetings.length) {
          toast.error("Selected meetings are not eligible for sign-off updates.");
          return;
        }
        setShowSignOffDialog(true);
        return;
      }
      setSelected(new Set());
      await queryClient.invalidateQueries({ queryKey: draftCalendarKeys.all() });
    } catch {
      toast.error("Bulk action failed. Please try again.");
    }
  }

  function getBulkStatusOptions() {
    const roleOptions: Record<string, string[]> = {
      admin: ["Business Approved", "Business Review", "DU Review", "Scheduled", "Resolved", "DU Declined", "Removed"],
      scheduler: ["Business Approved", "Business Review", "DU Review", "Scheduled", "Resolved", "DU Declined", "Removed"],
      approver: ["Business Approved", "DU Review", "Removed"],
      businessreviewer: ["Business Approved", "DU Review", "Removed"],
      viewonly: ["New Request", "Business Review", "Business Approved", "DU Review", "Scheduled", "Resolved", "DU Declined", "Removed"]
    };

    const roleScoped = roleOptions[userRole];
    if (roleScoped) {
      return roleScoped;
    }

    const existingStates = draftItems
      .map((item) => item.state)
      .filter((s): s is string => !!s && s.trim().length > 0);

    const fallbackStates = [
      "Business Approved",
      "Business Review",
      "DU Review",
      "Scheduled",
      "Resolved",
      BULK_STATUS_CONSTANTS.STATUS_DU_DECLINED,
      BULK_STATUS_CONSTANTS.STATUS_REMOVED
    ];

    return Array.from(new Set([...existingStates, ...fallbackStates]));
  }

  async function handleStatusDialogSubmit(values: BulkChangeStatusValues) {
    const selectedItems = draftItems.filter((item) => selected.has(item.id));
    const selectedIds = selectedItems.map((item) => item.id);
    const isApproverScopedRole = userRole === "approver" || userRole === "businessreviewer";
    const approverEligibleCurrentStatusesByTarget: Record<string, string[]> = {
      "Business Approved": ["New Request", "Business Review"],
      "DU Review": ["New Request", "Business Review"],
      Removed: ["New Request", "Business Review"]
    };

    let statusEligibleItems = selectedItems;
    if (isApproverScopedRole) {
      const allowedCurrentStates = approverEligibleCurrentStatusesByTarget[values.status] ?? [];
      if (allowedCurrentStates.length > 0) {
        const allowedSet = new Set(allowedCurrentStates.map((s) => s.toLowerCase()));
        statusEligibleItems = selectedItems.filter((item) =>
          allowedSet.has((item.state ?? "").trim().toLowerCase())
        );
      }
    }

    const draftIds = statusEligibleItems
      .filter((item) => item.state !== values.status)
      .map((item) => item.id);

    if (!draftIds.length) {
      toast.error("No Meeting(s) are there to update please try again.");
      return;
    }

    if (values.status === BULK_STATUS_CONSTANTS.STATUS_RESOLVED) {
      setPendingPushPayload({
        draftIds: selectedIds,
        status: values.status,
        comment: values.comment || "",
        userId
      });
      setShowStatusDialog(false);
      setShowPushConfirm(true);
      return;
    }

    const payload: BulkUpdateStatusRequest = {
      draftIds,
      status: values.status,
      comment: values.comment || "",
      duDeclineReason: values.duDeclineReason || "",
      duDeclineComments: values.duDeclineComments || "",
      userId
    };

    const updatedCount = await bulkUpdateStatus.mutateAsync(payload);

    if (updatedCount <= 0) {
      toast.error("No Meeting(s) are there to update please try again.");
      return;
    }

    const skipped = selectedItems.length - draftIds.length;
    const skippedPrefix = skipped > 0
      ? values.status === BULK_STATUS_CONSTANTS.STATUS_REMOVED
        ? `${skipped} items have been skipped. `
        : `${skipped} Meeting(s) could not be updated due to their current status. `
      : "";

    toast.success(`${skippedPrefix}${updatedCount} Meeting(s) has been updated successfully.`);
    setShowStatusDialog(false);
    setSelected(new Set());
    await queryClient.invalidateQueries({ queryKey: draftCalendarKeys.all() });
  }

  async function handleSignOffDialogSubmit(values: BulkSignOffValues) {
    const selectedItems = draftItems.filter((item) => selected.has(item.id));

    let eligible = selectedItems.filter((item) => item.signOffStatus?.toLowerCase() !== "signed off");
    if (userRole === "approver" || userRole === "businessreviewer") {
      eligible = eligible.filter((item) => item.canSubmitSignOff);
    }

    if (!eligible.length) {
      toast.error("No eligible meetings selected. Only valid statuses can be updated for SignOff.");
      return;
    }

    const result = await updateSignOffAction.mutateAsync({
      draftIds: eligible.map((item) => item.id),
      signOffAction: values.signOffAction
    });

    if (result <= 0) {
      toast.error("No meetings were updated. Please try again.");
      return;
    }

    const skipped = selectedItems.length - eligible.length;
    let message = `${result} meeting(s) have been signed off successfully.`;
    if (skipped > 0) {
      message += ` ${skipped} meeting(s) were skipped as they are not in a valid status for sign-off.`;
    }

    toast.success(message);
    setShowSignOffDialog(false);
    setSelected(new Set());
    await queryClient.invalidateQueries({ queryKey: draftCalendarKeys.all() });
  }

  async function handlePushToSummitConfirm() {
    if (!pendingPushPayload) return;

    const result = await bulkDraftPushToSummit.mutateAsync(pendingPushPayload);
    setShowPushConfirm(false);

    setPushTotalCount(pendingPushPayload.draftIds.length);
    setPushSuccessCount(result.successCount || 0);
    setFailedMeetings(result.bulkActionInValidStatusResponses || []);

    setShowPushResult(true);
    setPendingPushPayload(null);
    setSelected(new Set());
    await queryClient.invalidateQueries({ queryKey: draftCalendarKeys.all() });
  }

  // ── Bulk action menu items ─────────────────────────────────────────────────

  const bulkActions = useMemo(() => {
    const actions = [];
    if (canToggleSettings) {
      actions.push({ id: "add-grc", label: "Add to GRC" });
      actions.push({ id: "remove-grc", label: "Remove from GRC" });
    }
    actions.push({
      id: "change-status",
      label: "Change Status",
      disabled: signoffReqSubmitted && signOffProcessStarted && !isAdmin && !isScheduler,
      title: signoffReqSubmitted && signOffProcessStarted && !isAdmin && !isScheduler
        ? "Sign-off in progress – only Admins and Schedulers can change status"
        : undefined
    });
    if (responsibleForSignOff || isAdmin || isScheduler) {
      actions.push({ id: "change-signoff", label: "Change SignOff Status" });
    }
    return actions;
  }, [canToggleSettings, signoffReqSubmitted, signOffProcessStarted, isAdmin, isScheduler, responsibleForSignOff]);

  // ── Meeting button text ─────────────────────────────────────────────────────

  const addMeetingLabel =
    isAdmin || isScheduler || !signOffProcessStarted
      ? "Add Meeting"
      : signoffReqSubmitted && signOffProcessStarted
      ? "Request Meeting"
      : "Add Meeting";

  const filteredCount = useMemo(() => {
    const q = searchTerm.toLowerCase();
    return q
      ? draftItems.filter(
          (i) =>
            i.meetingName?.toLowerCase().includes(q) ||
            i.business?.toLowerCase().includes(q) ||
            i.meetingId?.toLowerCase().includes(q)
        ).length
      : draftItems.length;
  }, [draftItems, searchTerm]);

  return (
    <main className="flex flex-col" aria-label="Draft Calendar">
      {/* ── Toolbar ── */}
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-white px-3 py-2">
        {/* Fiscal year selector */}
        <div className="flex items-center gap-1.5">
          <label htmlFor="fy-select" className="text-xs font-medium text-slate-600 sr-only">
            Fiscal Year
          </label>
          {loadingFY ? (
            <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
          ) : (
            <select
              id="fy-select"
              value={activeFYId}
              onChange={(e) => { setSelectedFYId(Number(e.target.value)); setSelected(new Set()); }}
              className="rounded border border-slate-300 px-2 py-1 text-sm"
            >
              {fiscalYears.map((fy) => (
                <option key={fy.id} value={fy.id}>
                  {fy.code}
                  {fy.submissionStatus === "Closed" ? " (Closed)" : ""}
                </option>
              ))}
            </select>
          )}
        </div>

        <div className="h-5 w-px bg-slate-200" aria-hidden="true" />

        {/* Search */}
        <div className="relative">
          <label htmlFor="dc-search" className="sr-only">Search meetings</label>
          <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            id="dc-search"
            type="search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search meetings"
            className="h-8 w-64 rounded border border-slate-300 pl-8 pr-8 text-sm focus:outline-none focus:border-slate-500"
          />
          {searchTerm && (
            <button
              type="button"
              onClick={() => setSearchTerm("")}
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {/* Add Meeting - MOVED HERE (before right-aligned buttons) */}
        {canAddMeeting && !isFYClosed && (
          <button
            type="button"
            onClick={openCreate}
            className="rounded border border-slate-900 bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800"
          >
            {addMeetingLabel}
          </button>
        )}

        <div className="ml-auto flex items-center gap-2">
          {/* Refresh */}
          <button
            type="button"
            onClick={() => void refetch()}
            aria-label="Refresh"
            className="flex h-8 w-8 items-center justify-center rounded border border-slate-300 hover:bg-slate-100"
          >
            <RefreshCw className={`h-4 w-4 text-slate-600 ${isFetching ? "animate-spin" : ""}`} />
          </button>

          {/* Export */}
          <button
            type="button"
            aria-label="Export to Excel"
            className="flex h-8 w-8 items-center justify-center rounded border border-slate-300 text-slate-600 hover:bg-slate-100"
            title="Export list to Excel"
            onClick={() => toast("Excel export coming soon.")}
          >
            <Download className="h-4 w-4" />
          </button>

          {/* Bulk Actions */}
          {canBulkAction && (
            <BulkActionMenu
              actions={bulkActions}
              onAction={handleBulkAction}
              disabled={selected.size === 0}
            />
          )}

          {/* Calendar Options (Admin / Scheduler only) */}
          {canToggleSettings && (
            <div className="relative">
              <CalendarOptionsMenu
                canSubmit={canSubmit}
                signoffReqSubmitted={signoffReqSubmitted}
                signOffProcessStarted={signOffProcessStarted}
                isFYClosed={isFYClosed}
              />
            </div>
          )}
        </div>
      </div>

      {/* ── Status bar ── */}
      <div className="flex items-center gap-4 border-b border-slate-100 bg-slate-50 px-4 py-1.5 text-xs text-slate-600">
        <span>
          {selected.size > 0
            ? `${selected.size} selected of ${filteredCount}`
            : `${filteredCount} meeting${filteredCount !== 1 ? "s" : ""}`}
        </span>
        {isFetching && !isPending && (
          <span className="flex items-center gap-1 text-slate-400">
            <Loader2 className="h-3 w-3 animate-spin" /> Refreshing…
          </span>
        )}
        {isFYClosed && (
          <span className="rounded bg-red-100 px-2 py-0.5 text-red-700 font-medium">
            Fiscal Year Closed
          </span>
        )}
      </div>

      {/* ── Content ── */}
      <div className="flex-1 overflow-hidden px-0">
        {isPending ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            <span className="ml-3 text-sm text-slate-500">Loading calendar…</span>
          </div>
        ) : (
          <DraftCalendarTable
            items={draftItems}
            searchTerm={searchTerm}
            selected={selected}
            onSelectionChange={setSelected}
            onRowClick={openView}
            canEdit={canEditMeeting}
            canBulkAction={canBulkAction}
            canToggleSettings={canToggleSettings}
            signOffProcessStarted={signOffProcessStarted}
            isAdmin={isAdmin}
            isScheduler={isScheduler}
          />
        )}
      </div>

      {/* ── Meeting Flyout ── */}
      <BulkChangeStatusDialog
        open={showStatusDialog}
        statuses={getBulkStatusOptions()}
        onClose={() => setShowStatusDialog(false)}
        onSubmit={handleStatusDialogSubmit}
        submitting={bulkUpdateStatus.isPending || bulkDraftPushToSummit.isPending}
      />

      <BulkChangeSignOffStatusDialog
        open={showSignOffDialog}
        onClose={() => setShowSignOffDialog(false)}
        onSubmit={handleSignOffDialogSubmit}
        submitting={updateSignOffAction.isPending}
      />

      {showPushConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
          onClick={() => setShowPushConfirm(false)}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              setShowPushConfirm(false);
            }
          }}
          tabIndex={-1}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="push-confirm-title"
            className="w-full max-w-md rounded border border-slate-300 bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="border-b border-slate-200 px-4 py-3">
              <h2 id="push-confirm-title" className="text-base font-semibold text-slate-900">
                Confirm Push to Summit
              </h2>
            </div>
            <div className="px-4 py-4 text-sm text-slate-700">Are you sure you want to push selected meetings to Summit?</div>
            <div className="flex justify-end gap-2 border-t border-slate-200 px-4 py-3">
              <button
                type="button"
                onClick={() => setShowPushConfirm(false)}
                className="rounded border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
              >
                Go Back
              </button>
              <button
                type="button"
                onClick={() => void handlePushToSummitConfirm()}
                ref={pushConfirmPrimaryActionRef}
                className="rounded border border-slate-900 bg-slate-900 px-4 py-2 text-sm text-white hover:bg-slate-800"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      <BulkPushToSummitResultDialog
        open={showPushResult}
        onClose={() => setShowPushResult(false)}
        totalCount={pushTotalCount}
        successCount={pushSuccessCount}
        failedMeetings={failedMeetings}
      />

      {flyoutMeetingId !== null && (
        <MeetingFlyout
          meetingId={flyoutMeetingId}
          fiscalYear={activeFY}
          businesses={businesses}
          meetingTypes={meetingTypesMap}
          attendeeLevels={attendeeLevels}
          locations={locations}
          isViewOnly={flyoutViewOnly}
          lockedBy={lockedBy}
          canEdit={canEditMeeting}
          onEdit={() => setFlyoutViewOnly(false)}
          onSaved={handleFlyoutSaved}
          onClose={handleFlyoutClose}
        />
      )}
    </main>
  );
}

// ── Calendar Options dropdown (Submit / Sign Off / Close) ─────────────────────

interface CalendarOptionsProps {
  canSubmit: boolean;
  signoffReqSubmitted: boolean;
  signOffProcessStarted: boolean;
  isFYClosed: boolean;
}

function CalendarOptionsMenu({
  canSubmit,
  signoffReqSubmitted,
  signOffProcessStarted,
  isFYClosed
}: CalendarOptionsProps) {
  const [open, setOpen] = useState(false);

  const canSubmitCalendar = canSubmit && !signoffReqSubmitted;
  const canRequestSignOff = canSubmit && signoffReqSubmitted && !signOffProcessStarted;
  const canCloseCalendar = canSubmit && signoffReqSubmitted && isFYClosed;

  return (
    <div className="relative">
      <button
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 rounded border border-slate-700 bg-slate-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-600"
      >
        Calendar Options ▾
      </button>
      {open && (
        <ul
          role="menu"
          className="absolute right-0 top-full z-50 mt-1 w-48 rounded border border-slate-200 bg-white shadow-lg"
          onMouseLeave={() => setOpen(false)}
        >
          <li role="none">
            <button
              role="menuitem"
              type="button"
              disabled={!canSubmitCalendar}
              onClick={() => { setOpen(false); toast("Submit Calendar dialog coming soon."); }}
              className="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Submit Calendar
            </button>
          </li>
          <li role="none">
            <button
              role="menuitem"
              type="button"
              disabled={!canRequestSignOff}
              onClick={() => { setOpen(false); toast("Request Sign Off dialog coming soon."); }}
              className="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Request Sign Off
            </button>
          </li>
          <li role="none">
            <button
              role="menuitem"
              type="button"
              disabled={!canCloseCalendar}
              onClick={() => { setOpen(false); toast("Close Calendar dialog coming soon."); }}
              className="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Close Calendar
            </button>
          </li>
        </ul>
      )}
    </div>
  );
}
